import { Link } from "react-router-dom";
import { Star, Clock, Check, X, BookMarked } from "lucide-react";
import { Badge } from "./ui/badge";
import { motion } from "framer-motion";
import { useTranslation } from "../i18n";

export function MediaCard({ item, entry, showStatus = false, index = 0 }) {
  const { t } = useTranslation();
  const statusConfig = {
    planned: { icon: BookMarked, label: t.statuses.planned, className: "bg-gray-500/20 text-gray-400" },
    in_progress: { icon: Clock, label: t.statuses.in_progress, className: "bg-blue-500/20 text-blue-400" },
    completed: { icon: Check, label: t.statusesShort.completed, className: "bg-green-500/20 text-green-400" },
    dropped: { icon: X, label: t.statuses.dropped, className: "bg-red-500/20 text-red-400" },
  };
  const mediaItem = entry?.item || item;
  const status = entry?.status;
  const rating = entry?.rating;
  
  if (!mediaItem) return null;
  
  const StatusIcon = status ? statusConfig[status]?.icon : null;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="min-w-0"
    >
      <Link 
        to={`/media/${mediaItem.item_id}`} 
        className="group block min-h-[44px]" 
        data-testid={`media-card-${mediaItem.item_id}`}
      >
        <div className="relative aspect-[2/3] overflow-hidden rounded-lg border border-border/50 card-hover bg-muted shadow-sm">
          {mediaItem.cover_image || mediaItem.poster_url ? (
            <img 
              src={mediaItem.cover_image || mediaItem.poster_url} 
              alt={mediaItem.title} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
              loading="lazy" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <span className="text-3xl md:text-4xl text-muted-foreground/30">{mediaItem.title?.charAt(0)}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 hidden md:block">
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white text-sm font-medium truncate">{mediaItem.title}</p>
              {mediaItem.creator && <p className="text-white/70 text-xs truncate">{mediaItem.creator}</p>}
            </div>
          </div>
          {showStatus && status && (
            <div className="absolute top-1.5 left-1.5 md:top-2 md:left-2">
              <Badge className={`${statusConfig[status].className} text-[10px] md:text-xs px-1.5 py-0.5 md:px-2`}>
                <StatusIcon className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                <span className="hidden sm:inline">{statusConfig[status].label}</span>
              </Badge>
            </div>
          )}
          {entry?.priority && status === "planned" && (
            <div className="absolute bottom-1.5 left-1.5 md:bottom-2 md:left-2 z-10">
              <div className="bg-primary text-primary-foreground text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-sm" data-testid={`priority-badge-${entry.entry_id}`}>
                {entry.priority}
              </div>
            </div>
          )}
          {rating && (
            <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2">
              <Badge variant="secondary" className="bg-black/60 text-white border-0 text-[10px] md:text-xs px-1.5 py-0.5 md:px-2">
                <Star className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 fill-yellow-400 text-yellow-400" />
                {rating}
              </Badge>
            </div>
          )}
        </div>
        <div className="mt-1.5 md:mt-2 space-y-0.5 md:space-y-1">
          <h3 className="text-xs md:text-sm font-medium line-clamp-2 leading-tight group-hover:text-primary transition-colors">
            {mediaItem.title}
          </h3>
          <div className="flex items-center gap-1 md:gap-2 text-[10px] md:text-xs text-muted-foreground">
            <span className="font-medium uppercase tracking-wider truncate">
              {t.mediaTypesShort[mediaItem.media_type] || mediaItem.media_type}
            </span>
            {mediaItem.release_year && (
              <>
                <span>•</span>
                <span className="font-mono">{mediaItem.release_year}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
